/**
 * 🔊 Sound Debug — live spatial-audio truth (Sound Debug kit, 2026-07-10:
 * "spatial sound still isn't working — give me a debug setup").
 *
 * Reads the shared audioTrace ring (the DECISION-point record — every fire
 * logs which path it took: emitter-resolved / emitter-miss / xy /
 * spatial-no-position / non-spatial / raw-bypass) so "why was that full
 * volume?" is one table read, not an ear argument:
 *   - GREEN  emitter-resolved / xy — spatialized, gain shows the distance
 *   - YELLOW emitter-miss / spatial-no-position — unplaceable, erred quiet
 *   - RED    raw-bypass, or a spatial cue at gain 1.0, or LISTENER MISS
 *   - GREY   non-spatial — flat by design (UI, personal feedback cues)
 *
 * Test fires need NO second player: fire a spatial cue at known offsets from
 * the listener and check ears vs table (left quiet-ish, right panned, far
 * silent, miss quiet, flat control full volume).
 *
 * The 🔉 rings toggle draws an expanding ring at each positional fire in
 * world space (SoundDebugRenderer), gain-sized + path-colored.
 */
import React from 'react';
import {
  peekAudioTrace,
  audioTraceCounters,
  clearAudioTrace,
  isSoundRingsEnabled,
  toggleSoundRings,
  type AudioFireTrace,
  type AudioFirePath,
} from '../../../systems/audio/audioTrace';
import { gmBtnStyle, gmSectionTitleStyle } from './gmShared';
import { SoundDebugWindow } from '../SoundDebugWindow';

const PATH_COLOR: Record<AudioFirePath, string> = {
  'emitter-resolved': '#2ecc71',
  'xy': '#2ecc71',
  'emitter-miss': '#f1c40f',
  'spatial-no-position': '#f1c40f',
  'caller-provided': '#8a93a6',
  'non-spatial': '#8a93a6',
  'raw-bypass': '#e74c3c',
};

const PATH_SHORT: Record<AudioFirePath, string> = {
  'emitter-resolved': 'EMIT',
  'xy': 'XY',
  'emitter-miss': 'MISS',
  'spatial-no-position': 'NOPOS',
  'caller-provided': 'GIVEN',
  'non-spatial': 'FLAT',
  'raw-bypass': 'RAW',
};

const TEST_FIRES: Array<{ label: string; hint: string; fire: () => void }> = [
  { label: '📍 At you', hint: 'full volume, centered', fire: () => dbg().soundTestFire?.('xy', 0, 0) },
  { label: '⬅ 500px', hint: 'half volume, panned left', fire: () => dbg().soundTestFire?.('xy', -500, 0) },
  { label: '➡ 900px', hint: 'quiet, hard right', fire: () => dbg().soundTestFire?.('xy', 900, 0) },
  { label: '🌫 1500px', hint: 'SILENT (past max distance)', fire: () => dbg().soundTestFire?.('xy', 1500, 0) },
  { label: '👻 Bad emitter', hint: 'quiet (unresolvable → 15%)', fire: () => dbg().soundTestFire?.('emitter-miss') },
  { label: '🔔 Flat control', hint: 'non-spatial — full volume by design', fire: () => dbg().soundTestFire?.('flat') },
];

function dbg(): any {
  return (globalThis as any).__helixDebug ?? {};
}

/** A spatial cue that reached the engine at full volume is the bug signature. */
function isSuspicious(e: AudioFireTrace): boolean {
  return e.spatial && e.gain >= 0.99 && e.path !== 'caller-provided' && (e.distance == null || e.distance > 100);
}

export const GmSoundDebugContent: React.FC = () => {
  const [, bump] = React.useReducer((n: number) => n + 1, 0);
  const [ringsOn, setRingsOn] = React.useState(isSoundRingsEnabled());
  const [benchOpen, setBenchOpen] = React.useState(false);

  // 500ms poll — same cadence as the other GM debug panes.
  React.useEffect(() => {
    const t = setInterval(bump, 500);
    return () => clearInterval(t);
  }, []);

  const listener: { x: number; y: number } | null = dbg().soundListener?.() ?? null;
  const engine: {
    resumed?: boolean;
    wasmBooted?: boolean;
    bankLoaded?: boolean;
    disposed?: boolean;
    synth?: { contextState?: string; muted?: boolean; masterVolume?: number };
  } | null = dbg().soundEngineState?.() ?? null;
  const trace = peekAudioTrace(14).slice().reverse(); // newest first
  const counters = audioTraceCounters();

  // Silence with a healthy-looking trace = engine-level, not spatial-level.
  const ctxState = engine?.synth?.contextState ?? 'none';
  const engineSilent = !engine || ctxState !== 'running' || !!engine.synth?.muted ||
    (engine.synth?.masterVolume ?? 1) <= 0;

  const rowStyle: React.CSSProperties = {
    display: 'grid',
    gridTemplateColumns: '44px 1fr 44px 38px 38px',
    gap: 4,
    fontSize: 10,
    fontFamily: 'monospace',
    padding: '1px 0',
    alignItems: 'center',
  };

  return (
    <div style={{ padding: 10, color: '#ddd', fontSize: 12 }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
        <span data-testid="sound-debug-listener">
          🎧 Listener:{' '}
          {listener
            ? <span style={{ color: '#2ecc71' }}>({Math.round(listener.x)}, {Math.round(listener.y)})</span>
            : <span style={{ color: '#e74c3c', fontWeight: 'bold' }}>MISS — spatial cues err quiet (15%); reconnect/warp suspect</span>}
        </span>
      </div>

      {/* ENGINE strip — a suspended context / muted master is TOTAL silence
          while the trace still fills with normal-looking rows. */}
      <div data-testid="sound-debug-engine" style={{ marginBottom: 8, fontSize: 11 }}>
        ⚙️ Engine:{' '}
        {!engine ? (
          <span style={{ color: '#8a93a6' }}>not created — fires a test cue to build it</span>
        ) : engineSilent ? (
          <span style={{ color: '#e74c3c', fontWeight: 'bold' }}>
            SILENT — ctx {ctxState}
            {engine.synth?.muted ? ' · MUTED' : ''}
            {(engine.synth?.masterVolume ?? 1) <= 0 ? ' · master 0%' : ''}
            {' '}(click any test fire to unlock)
          </span>
        ) : (
          <span style={{ color: '#2ecc71' }}>
            ctx running · master {Math.round((engine.synth?.masterVolume ?? 1) * 100)}%
            {engine.wasmBooted ? ' · wasm' : ' · synth'}
            {engine.bankLoaded ? ' · bank' : ''}
          </span>
        )}
      </div>

      <div style={gmSectionTitleStyle}>Test fires (no second player needed)</div>
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 6, marginBottom: 10 }}>
        {TEST_FIRES.map(({ label, hint, fire }) => (
          <button key={label} style={gmBtnStyle(true)} onClick={() => { fire(); bump(); }} title={hint}>
            {label}
          </button>
        ))}
      </div>

      <div style={{ display: 'flex', gap: 6, marginBottom: 10 }}>
        <button
          data-testid="sound-debug-rings-toggle"
          style={{ ...gmBtnStyle(true), flex: 1, outline: ringsOn ? '2px solid #f1c40f' : 'none' }}
          onClick={() => setRingsOn(toggleSoundRings())}
          title="Draw an expanding ring at every positional fire, sized by gain, colored by path"
        >
          🔉 Emitter rings {ringsOn ? 'ON' : 'off'}
        </button>
        <button style={{ ...gmBtnStyle(true), flex: 1 }} onClick={() => { clearAudioTrace(); bump(); }}>
          🧹 Clear trace
        </button>
      </div>

      <div style={gmSectionTitleStyle}>
        Last fires — <span style={{ color: '#2ecc71' }}>{(counters['emitter-resolved'] ?? 0) + (counters['xy'] ?? 0)} spatial</span>
        {' · '}<span style={{ color: '#f1c40f' }}>{(counters['emitter-miss'] ?? 0) + (counters['spatial-no-position'] ?? 0)} quiet-fallback</span>
        {' · '}<span style={{ color: '#e74c3c' }}>{counters['raw-bypass'] ?? 0} raw</span>
        {' · '}<span style={{ color: '#8a93a6' }}>{counters['non-spatial'] ?? 0} flat</span>
      </div>
      <div data-testid="sound-debug-trace" style={{ maxHeight: 190, overflowY: 'auto' }}>
        {trace.length === 0 && (
          <div style={{ color: '#8a93a6', fontSize: 11 }}>
            No fires yet — hit a test button, or trigger any game sound.
          </div>
        )}
        {trace.map((e) => (
          <div key={e.seq} style={{ ...rowStyle, background: isSuspicious(e) ? 'rgba(231,76,60,0.18)' : 'transparent' }} title={e.note ?? ''}>
            <span style={{ color: PATH_COLOR[e.path], fontWeight: 'bold' }}>{PATH_SHORT[e.path]}</span>
            <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
              {e.cueId}{e.note ? ' ⓘ' : ''}
            </span>
            <span style={{ color: '#8a93a6' }}>{e.distance != null ? `${Math.round(e.distance)}px` : '—'}</span>
            <span style={{ color: '#8a93a6' }}>{e.pan >= 0 ? `R${e.pan.toFixed(1)}` : `L${(-e.pan).toFixed(1)}`}</span>
            <span style={{ color: e.gain >= 0.99 && e.spatial ? '#e74c3c' : '#ddd' }}>{Math.round(e.gain * 100)}%</span>
          </div>
        ))}
      </div>

      <div style={{ marginTop: 8, color: '#8a93a6', lineHeight: 1.5, fontSize: 11 }}>
        Red-highlighted rows = a SPATIAL cue that reached the engine at full
        volume — the bug signature. Hover a row for its note (ⓘ). Test fires
        play a bright two-note chirp (debug.sound_test — loud, unthrottled).
        Expected by ear: at-you loud · 500 left half-volume · 900 right quiet
        · 1500 silent · bad-emitter quiet · flat control loud. The table
        traces ALL game audio (race, abilities, UI), not just race cues.
      </div>

      {/* 🎛️ Bench fold — bus sliders + synth/bank cue browser (the former
          standalone Audio Lab card). LAZY: it owns a self-contained engine
          (own AudioContext), so it must not mount until opened. */}
      <div style={{ marginTop: 10 }}>
        <button
          data-testid="sound-debug-bench-toggle"
          style={{ ...gmBtnStyle(true), width: '100%', outline: benchOpen ? '2px solid #9b59b6' : 'none' }}
          onClick={() => setBenchOpen((v) => !v)}
          title="Bus gain sliders + fire any synth/bank cue by id — a self-contained bench engine, separate from the game's"
        >
          🎛️ Audio Lab bench {benchOpen ? '▾' : '▸'}
        </button>
        {benchOpen && (
          <div style={{ marginTop: 6, border: '1px solid #333', borderRadius: 6 }}>
            <SoundDebugWindow />
          </div>
        )}
      </div>
    </div>
  );
};
